(() => {
  if (customElements.get('arclo-newsletter')) return;
  const memory = {};
  const read = (key, persistent = false) => {try {return (persistent ? localStorage : sessionStorage).getItem(key) || memory[key];} catch {return memory[key];}};
  const write = (key, value, persistent = false) => {memory[key] = value;try {(persistent ? localStorage : sessionStorage).setItem(key, value);} catch { /* In-memory fallback when storage is blocked. */ }};
  class ArcloNewsletter extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const listen = (node, type, callback) => node?.addEventListener(type, callback, {signal:this.abort.signal});
      this.dialog = this.querySelector('dialog');
      this.tab = this.querySelector('[data-open]');
      this.form = this.querySelector('form');
      this.design = this.dataset.design === 'true';
      this.persistent = this.dataset.persistence === 'week';
      this.submitted = this.dataset.known === 'true' || !!read('arclo-newsletter-submitted', true);
      this.tab && (this.tab.hidden = false);
      if (this.submitted) this.showSuccess();
      listen(this.tab, 'click', () => this.open());
      this.querySelectorAll('[data-close]').forEach(el => listen(el, 'click', () => this.close()));
      listen(this.dialog, 'cancel', event => {event.preventDefault();this.close();});
      listen(this.querySelector('[data-verification-backdrop]'),'click',()=>this.close());
      listen(document,'focusin',event=>{
        if(!this.verifying||!this.dialog.open||this.dialog.contains(event.target))return;
        // Shopify renders its verification iframe outside the form. Never trap it behind our dialog.
        if(event.target.tagName==='IFRAME'&&/captcha/i.test(event.target.title+' '+event.target.src))return;
        this.querySelector('[data-close]').focus({preventScroll:true});
      });
      listen(this.dialog,'keydown',event=>{
        if(!this.verifying)return;
        if(event.key==='Escape'){event.preventDefault();this.close();return;}
        if(event.key==='Tab'){
          const controls=[...this.dialog.querySelectorAll('button:not(:disabled), input:not([type="hidden"]), a[href]')].filter(el=>el.getClientRects().length);
          if(event.shiftKey&&document.activeElement===controls[0]){event.preventDefault();controls.at(-1)?.focus();}
          else if(!event.shiftKey&&document.activeElement===controls.at(-1)){event.preventDefault();controls[0]?.focus();}
        }
      });
      listen(this.dialog, 'click', event => {if (event.target !== this.dialog) return;const rect=this.dialog.getBoundingClientRect();if(event.clientX<rect.left||event.clientX>rect.right||event.clientY<rect.top||event.clientY>rect.bottom)this.close();});
      listen(this.form, 'submit', event => {
        if (this.design) {event.preventDefault();this.error('Open the theme preview to test a real subscription.');return;}
        if (this.busy) {
          // Shopify can resubmit the same form after completing its challenge.
          const verified=!!this.form.querySelector('[name="h-captcha-response"]')?.value;
          if(verified&&!this.allowedVerifiedSubmit){this.allowedVerifiedSubmit=true;return;}
          event.preventDefault();return;
        }
        if (!this.form.reportValidity()) {event.preventDefault();return;}
        this.busy = true;
        // Leave the browser top layer so Shopify's body-mounted CAPTCHA remains usable.
        // Keep our backdrop, scroll lock and keyboard containment; never handle the challenge itself.
        this.dialog.close();this.dialog.classList.add('arclo-newsletter-verifying');this.dialog.show();
        this.verifying=true;this.querySelector('[data-verification-backdrop]').hidden=false;
        write('arclo-newsletter-pending', String(Date.now()));
        const button = this.querySelector('[data-submit]');this.buttonText=button.textContent;button.disabled=true;button.textContent='SUBMITTING…';
        // Native POST deliberately preserves Shopify's CAPTCHA and consent flow.
        this.submitTimer=setTimeout(() => {if(this.isConnected){this.resetSubmit();this.error('Please complete Shopify’s security verification if prompted. If it does not load, check your connection and try again.');}},30000);
      });
      listen(window, 'pageshow', () => this.resetSubmit());
      listen(this.querySelector('[data-copy]'), 'click', async event => {
        const button=event.currentTarget;
        try {await navigator.clipboard.writeText(this.querySelector('[data-code]').textContent.trim());button.textContent='COPIED';}
        catch {const status=this.querySelector('[data-status]');status.hidden=false;status.textContent='Copy unavailable. Select the code above and copy it manually.';}
      });
      listen(document, 'shopify:section:select', event => {if(event.target.contains(this))this.open();});
      listen(document, 'shopify:section:deselect', event => {if(event.target.contains(this))this.close();});
      const pending=Number(read('arclo-newsletter-pending'));
      const parameters=new URL(location.href).searchParams;
      const returned=parameters.has('customer_posted')||parameters.get('form_type')==='customer';
      // Only a Shopify-rendered success after this browser's real form submission counts.
      if (this.querySelector('[data-server-success]') && pending && Date.now()-pending<30*60*1000) {
        write('arclo-newsletter-submitted','true',true);write('arclo-newsletter-pending','');this.submitted=true;this.showSuccess();this.open();
      } else if (!this.querySelector('[data-error]').hidden) {this.open();}
      else if (returned && pending) {this.open();this.error('We could not confirm your subscription. Please check for a confirmation email, or try again.');}
      else if (!this.design && !this.submitted && this.dataset.cart!=='true') {
        const dismissed=Number(read('arclo-newsletter-dismissed',this.persistent));
        if (!dismissed || (this.persistent && Date.now()-dismissed>7*86400000)) {
          this.due=Date.now()+Number(this.dataset.delay)*1000;
          const attempt=()=>{if(!this.cancelAuto&&!document.hidden&&Date.now()>=this.due&&!document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]'))this.open();};
          this.timer=setTimeout(attempt,Number(this.dataset.delay)*1000);
          listen(document,'visibilitychange',attempt);
        }
      }
    }
    open() {
      if(this.dialog.open)return;
      this.cancelAuto=true;
      clearTimeout(this.timer);
      this.previousFocus=document.activeElement;
      this.previousOverflow=document.documentElement.style.overflow;
      this.dialog.showModal();document.documentElement.style.overflow='hidden';
      this.tab && (this.tab.hidden=true);
      (this.submitted?this.querySelector('[data-success-heading]'):this.querySelector('input[type="email"]'))?.focus({preventScroll:true});
    }
    close() {
      clearTimeout(this.timer);
      this.cancelAuto=true;
      if(this.dialog.open){this.dialog.close();document.documentElement.style.overflow=this.previousOverflow||'';}
      this.verifying=false;this.dialog.classList.remove('arclo-newsletter-verifying');this.querySelector('[data-verification-backdrop]').hidden=true;
      write('arclo-newsletter-dismissed',String(Date.now()),this.persistent);
      this.tab && (this.tab.hidden=false);
      if(this.previousFocus?.isConnected && this.previousFocus!==document.body)this.previousFocus.focus({preventScroll:true});
      else this.tab?.focus({preventScroll:true});
    }
    showSuccess() {this.querySelector('[data-signup]').hidden=true;this.querySelector('[data-success]').hidden=false;this.dialog.setAttribute('aria-labelledby',this.querySelector('[data-success-heading]').id=this.id+'-success');}
    error(message) {const el=this.querySelector('[data-error]');el.textContent=message;el.hidden=false;}
    resetSubmit() {clearTimeout(this.submitTimer);this.busy=false;const button=this.querySelector('[data-submit]');button.disabled=false;if(this.buttonText)button.textContent=this.buttonText;}
    disconnectedCallback() {clearTimeout(this.timer);clearTimeout(this.submitTimer);if(this.dialog?.open){this.dialog.close();document.documentElement.style.overflow=this.previousOverflow||'';}this.abort?.abort();this.abort=null;}
  }
  customElements.define('arclo-newsletter',ArcloNewsletter);
})();
